import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { deletePost, getPosts, savePost, slugify } from '../lib/blogStorage';

const emptyPost = () => ({
  id: '',
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  published: true,
  createdAt: new Date().toISOString(),
});

export default function Admin() {
  const [posts, setPosts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [form, setForm] = useState(emptyPost());
  const [auth, setAuth] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    const storedPassword = window.localStorage.getItem('synex_admin_pass');
    if (storedPassword) {
      setPassword(storedPassword);
      setAuth(true);
    }
  }, []);

  useEffect(() => {
    if (!auth) return;

    let active = true;
    getPosts({ published: false })
      .then((data) => {
        if (!active) return;
        setPosts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to load posts', err);
      });

    return () => {
      active = false;
    };
  }, [auth]);

  const activePost = useMemo(() => posts.find((p) => p.id === activeId), [posts, activeId]);

  useEffect(() => {
    if (activePost) {
      setForm(activePost);
    }
  }, [activePost]);

  const resetForm = () => {
    setActiveId(null);
    setForm(emptyPost());
  };

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    const normalized = {
      ...form,
      slug: slugify(form.title || form.slug),
      id: form.id || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    };

    if (!normalized.title.trim()) return;

    try {
      await savePost(normalized, password);
      const latest = await getPosts({ published: false });
      setPosts(Array.isArray(latest) ? latest : []);
      resetForm();
    } catch (error) {
      console.error('Failed to save post', error);
      alert('Unable to save post. Check console for details.');
    }
  };
  const onLogin = () => {
    // Simple auth guard: the real auth is performed on the server via x-admin-secret header
    if (!password.trim()) {
      alert('Enter a password');
      return;
    }

    window.localStorage.setItem('synex_admin_pass', password);
    setAuth(true);
  };

  const onLogout = () => {
    window.localStorage.removeItem('synex_admin_pass');
    setAuth(false);
    setPassword('');
  };

  const onDelete = async (id) => {
    try {
      await deletePost(id, password);
      const latest = await getPosts({ published: false });
      setPosts(Array.isArray(latest) ? latest : []);
      if (activeId === id) resetForm();
    } catch (error) {
      console.error('Failed to delete post', error);
      alert('Unable to delete post. Check the console for details.');
    }
  };

  if (!auth) {
    return (
      <section className="py-24 px-6 min-h-[80vh]">
        <Helmet>
          <title>Admin Login – Synex Agency</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="max-w-md mx-auto">
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 shadow-sm">
            <h1 className="text-2xl font-semibold text-zinc-900 mb-4">Admin Login</h1>
            <p className="text-zinc-500 mb-6">Enter the admin password to manage blog posts.</p>

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand"
            />

            <button
              onClick={onLogin}
              className="mt-6 w-full rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90"
            >
              Log in
            </button>

            <p className="mt-4 text-xs text-zinc-400">
              Tip: the password is <span className="font-semibold">synexadmin123</span>.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 px-6 min-h-[80vh]">
      <Helmet>
        <title>Admin Panel – Synex Agency</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-[1200px] mx-auto">
        <header className="mb-14 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-semibold tracking-tight text-zinc-900">Admin Panel</h1>
            <p className="text-zinc-500 mt-3">
              Manage blog posts and boost SEO. Your changes save locally in the browser.
            </p>
          </div>

          <button
            onClick={onLogout}
            className="rounded-full border border-zinc-200 bg-white px-6 py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
          >
            Logout
          </button>
        </header>

        <div className="grid gap-10 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4">Create / Edit Post</h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-zinc-600">Title</label>
                  <input
                    value={form.title}
                    onChange={(e) => onChange('title', e.target.value)}
                    className="mt-2 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand"
                    placeholder="Post title"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-zinc-600">Excerpt</label>
                  <textarea
                    value={form.excerpt}
                    onChange={(e) => onChange('excerpt', e.target.value)}
                    rows={3}
                    className="mt-2 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand"
                    placeholder="Short summary for listings"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-zinc-600">Content</label>
                  <textarea
                    value={form.content}
                    onChange={(e) => onChange('content', e.target.value)}
                    rows={6}
                    className="mt-2 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand"
                    placeholder="Write markdown-style content here..."
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm font-semibold text-zinc-600">
                    <input
                      type="checkbox"
                      checked={form.published}
                      onChange={(e) => onChange('published', e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-300 text-brand"
                    />
                    Published
                  </label>
                  <button
                    onClick={resetForm}
                    type="button"
                    className="rounded-full border border-brand/30 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-brand/10"
                  >
                    Clear
                  </button>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={onSave}
                    type="button"
                    className="rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90"
                  >
                    Save Post
                  </button>
                  {activeId && (
                    <button
                      onClick={() => {
                        onDelete(activeId);
                      }}
                      type="button"
                      className="rounded-full border border-red-200 bg-red-50 px-6 py-3 text-sm font-semibold text-red-600 hover:bg-red-100"
                    >
                      Delete Post
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-zinc-900 mb-4">Draft instructions</h2>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li>Use a clear title (short and search-friendly).</li>
                <li>Set a compelling excerpt that appears on the blog list.</li>
                <li>Write in short paragraphs and use markdown-like new lines.</li>
                <li>Toggle “Published” to control visibility.</li>
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-zinc-900">All Posts</h2>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setActiveId(null);
                }}
                className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
              >
                New
              </button>
            </div>

            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-start justify-between gap-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm"
                >
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-zinc-900">{post.title}</span>
                      <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{post.excerpt}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveId(post.id)}
                      className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(post.id)}
                      className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
